import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { invoicesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { cn } from '../../lib/utils';
import { format } from 'date-fns';
import { getEffectiveInvoiceStatus } from '../../lib/invoiceStatus';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  CirclePoundSterling,
  Copy,
  CreditCard,
  Filter,
  Printer,
  Send,
  Trash2,
} from 'lucide-react';
import InvoicePaymentForm from '../../components/invoices/InvoicePaymentForm';
import BulkPaymentModal from '../../components/invoices/BulkPaymentModal';
import PaymentHistoryModal from '../../components/invoices/PaymentHistoryModal';

const Invoices = () => {
  const navigate = useNavigate();
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewFilter, setViewFilter] = useState('active');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [paymentModalInvoice, setPaymentModalInvoice] = useState(null);
  const [paymentHistoryInvoice, setPaymentHistoryInvoice] = useState(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false);
  const headerCheckboxRef = useRef(null);
  const [clientFilter, setClientFilter] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRefs = useRef({});
  const dropdownMenuRef = useRef(null);
  const [duplicatingInvoiceId, setDuplicatingInvoiceId] = useState(null);

  const selectableInvoices = invoices.filter(
    (inv) => (inv.balanceDue ?? 0) > 0 && !inv.isDeleted
  );
  const selectedWithBalance = invoices.filter(
    (inv) => selectedInvoiceIds.has(inv.id) && (inv.balanceDue ?? 0) > 0 && !inv.isDeleted
  );

  const toggleSelect = (id) => {
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedInvoiceIds.size >= selectableInvoices.length) {
      setSelectedInvoiceIds(new Set());
    } else {
      setSelectedInvoiceIds(new Set(selectableInvoices.map((inv) => inv.id)));
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, search, statusFilter, viewFilter, clientFilter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const activeRef = openDropdownId ? dropdownRefs.current[openDropdownId] : null;
      const clickedInsideMenu = dropdownMenuRef.current?.contains(e.target);
      if (activeRef && !activeRef.contains(e.target) && !clickedInsideMenu) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  useEffect(() => {
    if (!openDropdownId) {
      setDropdownPosition(null);
      return;
    }
    const el = dropdownRefs.current[openDropdownId];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left });

    const closeOnScroll = () => setOpenDropdownId(null);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [openDropdownId]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        selectedInvoiceIds.size > 0 &&
        selectableInvoices.length > 0 &&
        selectedInvoiceIds.size < selectableInvoices.length;
    }
  }, [selectedInvoiceIds, selectableInvoices.length]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await invoicesAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search,
        status: statusFilter || undefined,
        view: viewFilter,
        clientId: clientFilter || undefined,
      });
      
      setInvoices(response.invoices);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (err) {
      setError(err.message || 'Failed to load invoices');
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await invoicesAPI.delete(invoiceId);
      fetchInvoices();
    } catch (err) {
      alert(err.message || 'Failed to delete invoice');
      console.error('Delete error:', err);
    }
  };

  const handleArchiveInvoice = async (invoiceId, isArchived) => {
    try {
      await invoicesAPI.archive(invoiceId, !isArchived);
      fetchInvoices();
    } catch (err) {
      alert(err.message || 'Failed to update invoice');
      console.error('Archive error:', err);
    }
  };

  const getClientDisplayName = (client) => {
    if (client.clientType === 'company') {
      return client.companyName || 'Unnamed Company';
    }
    return `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Individual';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatInvoiceDate = (dateStr) => {
    if (!dateStr) return '—';
    return format(new Date(dateStr), 'dd/MM/yy');
  };

  const getStatusTriggerClass = (invoice) => {
    if (invoice.isDeleted) {
      return 'bg-[#E15230] text-white border-[#E15230] hover:opacity-95';
    }
    const s = getEffectiveInvoiceStatus({ status: invoice.status, dueDate: invoice.dueDate });
    if (s === 'paid-in-full') {
      return 'bg-[var(--ds-status-success)] text-white border-transparent hover:opacity-95';
    }
    if (s === 'overdue') {
      return 'bg-[var(--ds-status-danger)] text-white border-transparent hover:opacity-95';
    }
    if (s === 'part-paid') {
      return 'bg-[var(--ds-status-warning)] text-white border-transparent hover:opacity-95';
    }
    if (s === 'sent') {
      return 'bg-amber-600 text-white border-transparent hover:opacity-95';
    }
    return 'bg-[var(--ds-accent-primary)] text-white border-[var(--ds-accent-primary)] hover:opacity-95';
  };

  const getStatusTriggerLabel = (invoice) => {
    if (invoice.isDeleted) return 'DELETED';
    const display = getEffectiveInvoiceStatus({ status: invoice.status, dueDate: invoice.dueDate });
    const base = display.replace(/-/g, ' ');
    if (invoice.isArchived) return `${base.toUpperCase()} · ARCH`;
    return base.toUpperCase();
  };

  const handleFilterOnClient = (clientId) => {
    setOpenDropdownId(null);
    if (!clientId) return;
    setClientFilter(clientId);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDuplicateInvoice = async (invoice) => {
    setOpenDropdownId(null);
    try {
      setDuplicatingInvoiceId(invoice.id);
      const data = await invoicesAPI.duplicate(invoice.id);
      const newId = data?.invoice?.id;
      if (!newId) throw new Error('Duplicate failed');
      await fetchInvoices();
      navigate(`/tenant/invoices/${newId}`);
    } catch (err) {
      alert(err.message || 'Failed to duplicate invoice');
      console.error(err);
    } finally {
      setDuplicatingInvoiceId(null);
    }
  };

  const handlePrintInvoice = async (invoice) => {
    setOpenDropdownId(null);
    try {
      const html = await invoicesAPI.getHtml(invoice.id);
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(typeof html === 'string' ? html : '');
      w.document.close();
      setTimeout(() => {
        w.focus();
        w.print();
      }, 300);
    } catch (err) {
      alert(err.message || 'Failed to open print view');
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Invoices
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage your invoices and billing
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/tenant/invoices/new')}
              >
                + New Invoice
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <FormLabel>Search</FormLabel>
              <Input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Search by invoice number, reference, or client..."
                className="h-8"
              />
            </div>
            <div>
              <FormLabel>Status</FormLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="part-paid">Part Paid</option>
                <option value="paid-in-full">Paid in Full</option>
                <option value="overdue">Overdue</option>
              </Select>
            </div>
            <div>
              <FormLabel>View</FormLabel>
              <Select
                value={viewFilter}
                onChange={(e) => {
                  setViewFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="deleted">Deleted</option>
              </Select>
            </div>
            {clientFilter && (
              <div className="md:col-span-4 flex items-center gap-2">
                <span className="text-micro text-[var(--ds-text-muted)]">Client filter active</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setClientFilter('');
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {/* Invoice List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-xs text-gray-500">Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <Card className="text-center py-12">
            <p className="text-xs text-[var(--ds-text-muted)]">No invoices found</p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/tenant/invoices/new')}
              className="mt-4"
            >
              Create Your First Invoice
            </Button>
          </Card>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => selectedWithBalance.length > 0 && setShowBulkPaymentModal(true)}
                disabled={selectedWithBalance.length === 0}
                className="inline-flex items-center justify-center h-8 px-3 border border-[var(--ds-control-field-border)] rounded-md bg-[var(--ds-control-field-bg)] text-xs font-medium text-gray-700 dark:text-gray-300 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              >
                <CirclePoundSterling className="h-4 w-4 mr-2" />
                Log payment for selected ({selectedWithBalance.length})
              </button>
            </div>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-[var(--ds-border-divider)]">
                {/* Header row */}
                <li className="px-4 py-2 sm:px-6 bg-[var(--ds-control-field-bg)] border-b border-[var(--ds-border-divider)]">
                  <div className="flex items-center gap-4">
                    <div className="min-w-[28px] flex justify-center">
                      <input
                        type="checkbox"
                        checked={
                          selectableInvoices.length > 0 &&
                          selectedInvoiceIds.size >= selectableInvoices.length
                        }
                        ref={headerCheckboxRef}
                        onChange={toggleSelectAll}
                        disabled={selectableInvoices.length === 0}
                        className="invoice-list-checkbox h-4 w-4 rounded border border-gray-300 bg-white accent-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                        aria-label="Select all"
                      />
                    </div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[100px]">Invoice</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] flex-1 min-w-0">Client</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[100px] shrink-0">Reference</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[88px] shrink-0">Date Issued</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[88px] shrink-0">Due Date</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[80px] text-right shrink-0">Amount</div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[104px]">Status</div>
                      <div className="text-xs font-medium text-[var(--ds-text-secondary)]">View</div>
                    </div>
                  </div>
                </li>
                {invoices.map((invoice) => (
                  <li key={invoice.id}>
                    <div className="px-4 py-3 sm:px-6 hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180">
                      <div className="flex items-center gap-4">
                        {/* Checkbox */}
                        <div className="min-w-[28px] flex justify-center shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedInvoiceIds.has(invoice.id)}
                            onChange={() => toggleSelect(invoice.id)}
                            disabled={(invoice.balanceDue ?? 0) <= 0 || invoice.isDeleted}
                            className="invoice-list-checkbox h-4 w-4 rounded border border-gray-300 bg-white accent-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Select invoice ${invoice.invoiceNum}`}
                          />
                        </div>
                        {/* Invoice Number */}
                        {invoice.isDeleted ? (
                          <div className="text-xs font-medium text-[var(--ds-text-muted)] min-w-[100px] truncate">
                            {invoice.invoiceNum}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(`/tenant/invoices/${invoice.id}`)}
                            className="min-w-[100px] max-w-[140px] truncate text-left text-xs font-medium text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            {invoice.invoiceNum}
                          </button>
                        )}
                        
                        {/* Client Name */}
                        <div className="text-xs text-[var(--ds-text-muted)] flex-1 min-w-0 truncate">
                          {getClientDisplayName(invoice.client)}
                        </div>
                        
                        {/* Reference */}
                        <div className="text-xs text-[var(--ds-text-muted)] min-w-[100px] shrink-0 truncate">
                          {invoice.reference || '—'}
                        </div>

                        {/* Date Issued */}
                        <div className="text-xs text-[var(--ds-text-muted)] min-w-[88px] shrink-0 tabular-nums">
                          {formatInvoiceDate(invoice.dateIssued)}
                        </div>

                        {/* Due Date */}
                        <div className="text-xs text-[var(--ds-text-muted)] min-w-[88px] shrink-0 tabular-nums">
                          {formatInvoiceDate(invoice.dueDate)}
                        </div>

                        {/* Amount */}
                        <div className="text-xs font-semibold text-[var(--ds-text-primary)] tabular-nums text-right min-w-[80px] shrink-0">
                          {formatCurrency(invoice.total)}
                        </div>
                        
                        {/* Status + View */}
                        <div className="flex items-center gap-2 shrink-0">
                          <div
                            className="relative inline-block shrink-0"
                            ref={(el) => {
                              dropdownRefs.current[invoice.id] = el;
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setOpenDropdownId(openDropdownId === invoice.id ? null : invoice.id)
                              }
                              className={cn(
                                'inline-flex h-6 min-w-[104px] max-w-[152px] items-center justify-between gap-0.5 rounded-[4px] border px-1.5 py-0 text-xs font-semibold uppercase tracking-wide leading-none shadow-none transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-surface-panel)]',
                                getStatusTriggerClass(invoice)
                              )}
                              aria-expanded={openDropdownId === invoice.id}
                              aria-haspopup="menu"
                            >
                              <span className="truncate">{getStatusTriggerLabel(invoice)}</span>
                              <ChevronDown className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                            </button>
                            {openDropdownId === invoice.id &&
                              dropdownPosition &&
                              createPortal(
                                <div
                                  ref={dropdownMenuRef}
                                  className="fixed z-[9999] min-w-[200px] max-w-[260px] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] py-1 shadow-lg"
                                  role="menu"
                                  style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleDuplicateInvoice(invoice)}
                                    disabled={duplicatingInvoiceId === invoice.id}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                                    role="menuitem"
                                  >
                                    <Copy className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                    {duplicatingInvoiceId === invoice.id ? 'Copying…' : 'Make a copy'}
                                  </button>
                                  {!invoice.isDeleted &&
                                    (invoice.balanceDue ?? 0) > 0 &&
                                    invoice.status !== 'paid-in-full' && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenDropdownId(null);
                                          setPaymentModalInvoice(invoice);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                        role="menuitem"
                                      >
                                        <CreditCard className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                        Log a payment
                                      </button>
                                    )}
                                  {!invoice.isDeleted && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        navigate(`/tenant/invoices/${invoice.id}/send`);
                                      }}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      <Send className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      Send invoice
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handlePrintInvoice(invoice)}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                    role="menuitem"
                                  >
                                    <Printer className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                    Print
                                  </button>
                                  {invoice.client?.id && (
                                    <button
                                      type="button"
                                      onClick={() => handleFilterOnClient(invoice.client.id)}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      <Filter className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      Filter on client
                                    </button>
                                  )}
                                  {!invoice.isDeleted && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        handleArchiveInvoice(invoice.id, invoice.isArchived);
                                      }}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      {invoice.isArchived ? (
                                        <ArchiveRestore className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      ) : (
                                        <Archive className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      )}
                                      {invoice.isArchived ? 'Unarchive' : 'Archive'}
                                    </button>
                                  )}
                                  {!invoice.isDeleted && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        handleDeleteInvoice(invoice.id);
                                      }}
                                      className="mx-1 mt-0.5 flex w-[calc(100%-0.5rem)] items-center gap-2 rounded-[var(--ds-radius-sm)] bg-[var(--ds-status-danger)] px-3 py-2 text-left text-xs font-medium text-white hover:opacity-95"
                                      role="menuitem"
                                    >
                                      <Trash2 className="h-4 w-4 shrink-0 text-white" />
                                      Delete
                                    </button>
                                  )}
                                </div>,
                                document.body
                              )}
                          </div>
                          <button
                            type="button"
                            onClick={() => navigate(`/tenant/invoices/${invoice.id}/view`)}
                            className="text-xs font-medium text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] hover:underline shrink-0"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Card className="mt-6">
                <CardContent className="px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs text-[var(--ds-text-secondary)]">
                      Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      of <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-[var(--ds-radius-md)] shadow-sm -space-x-px" aria-label="Pagination">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="rounded-l-[var(--ds-radius-md)] rounded-r-none"
                      >
                        Previous
                      </Button>
                      {[...Array(pagination.totalPages)].map((_, i) => (
                        <Button
                          key={i + 1}
                          variant={pagination.page === i + 1 ? 'primary' : 'outline'}
                          size="sm"
                          onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                          className="rounded-none"
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page >= pagination.totalPages}
                        className="rounded-r-[var(--ds-radius-md)] rounded-l-none"
                      >
                        Next
                      </Button>
                    </nav>
                  </div>
                </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Payment Modal */}
      {paymentModalInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Log Payment</h2>
              <button
                onClick={() => setPaymentModalInvoice(null)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl transition-colors"
              >
                ×
              </button>
            </div>
            <InvoicePaymentForm
              invoiceId={paymentModalInvoice.id}
              balanceDue={paymentModalInvoice.balanceDue}
              onSuccess={async () => {
                setPaymentModalInvoice(null);
                await fetchInvoices();
              }}
              onCancel={() => setPaymentModalInvoice(null)}
            />
          </div>
        </div>
      )}

      {/* Bulk Payment Modal */}
      <BulkPaymentModal
        invoices={selectedWithBalance}
        open={showBulkPaymentModal}
        onClose={() => setShowBulkPaymentModal(false)}
        onSuccess={async () => {
          setSelectedInvoiceIds(new Set());
          await fetchInvoices();
        }}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        invoiceId={paymentHistoryInvoice?.id}
        invoiceNum={paymentHistoryInvoice?.invoiceNum}
        open={!!paymentHistoryInvoice}
        onClose={() => setPaymentHistoryInvoice(null)}
      />
    </Layout>
  );
};

export default Invoices;

